import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Input,
  SimpleGrid,
  useColorModeValue,
} from '@chakra-ui/react';
import { AddIcon } from '@chakra-ui/icons';
import SupplierList from '../components/SupplierList';
import SupplierModals from '../components/SupplierModals';
import ConfirmationModal from '../components/ConfirmationModal';
import DebtModal from '../components/DebtModal';
import usePagination from '../utils/usePagination';
import { getVisiblePageNumbers } from '../utils/paginationHelpers';
import api from '../api';

const Suppliers = () => {
  // Color mode values for modern styling
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.700', 'gray.200');
  const subtleTextColor = useColorModeValue('gray.600', 'gray.400');
  const shadowColor = useColorModeValue('rgba(0, 0, 0, 0.1)', 'rgba(0, 0, 0, 0.3)');
  const filterBg = useColorModeValue('white', 'gray.800');
  
  const [suppliers, setSuppliers] = useState([]);
  const [search, setSearch] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone_number: '',
    edrpou_code: '',
    address: '',
    bank_name: '',
    bank_number: '',
  });
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDebtModalOpen, setIsDebtModalOpen] = useState(false);

  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [supplierToDelete, setSupplierToDelete] = useState(null);
  const [selectedSupplier, setSelectedSupplier] = useState(null);

  const fetchSuppliers = async (config) => {
    try {
      const requestConfig = typeof config === 'string' ? { url: config } : config || {};
      const rawUrl = requestConfig.url || '/suppliers/';
      const explicitPage = requestConfig.page;

      const ensureLeadingSlash = (value) => (value.startsWith('/') ? value : `/${value}`);
      const normalizePath = (value) => {
        let normalized = ensureLeadingSlash(value || '/suppliers/');
        if (normalized.startsWith('/api/')) {
          normalized = normalized.replace('/api', '');
        }
        if (!normalized.endsWith('/')) {
          normalized = `${normalized}/`;
        }
        return normalized;
      };

      let pathname = '/suppliers/';
      let params = new URLSearchParams();

      if (rawUrl.startsWith('http')) {
        const absoluteUrl = new URL(rawUrl);
        pathname = normalizePath(absoluteUrl.pathname);
        params = new URLSearchParams(absoluteUrl.search);
      } else {
        const [pathPart, searchPart] = rawUrl.split('?');
        pathname = normalizePath(pathPart || '/suppliers/');
        params = new URLSearchParams(searchPart || '');
      }

      let requestedPage = 1;

      if (explicitPage !== undefined) {
        params.set('page', explicitPage);
        requestedPage = explicitPage;
      } else if (params.has('page')) {
        const parsedPage = Number(params.get('page'));
        if (Number.isNaN(parsedPage) || parsedPage < 1) {
          params.delete('page');
          requestedPage = 1;
        } else {
          requestedPage = parsedPage;
        }
      }

      const trimmedSearch = search.trim();
      if (trimmedSearch) {
        params.set('search', trimmedSearch);
      } else {
        params.delete('search');
      }

      const queryString = params.toString();
      const requestUrl = queryString ? `${pathname}?${queryString}` : pathname;

      const response = await api.get(requestUrl);
      const results = response.data.results || [];
      setSuppliers(results);

      const shouldUpdatePageSize =
        Array.isArray(results) &&
        results.length > 0 &&
        (response.data.next || !response.data.previous);

      updatePagination(response.data, {
        currentPage: requestedPage,
        pageSize: shouldUpdatePageSize ? results.length : undefined,
      });
    } catch (error) {
      console.error('Failed to fetch suppliers:', error);
    }
  };

  const { pagination, updatePagination, fetchNext, fetchPrevious, goToPage } = usePagination(fetchSuppliers, '/suppliers/');

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(pagination.count / (pagination.pageSize || 1))),
    [pagination.count, pagination.pageSize],
  );

  const visiblePages = useMemo(
    () => getVisiblePageNumbers(pagination.currentPage, totalPages),
    [pagination.currentPage, totalPages],
  );

  useEffect(() => {
    fetchSuppliers();
  }, [search]);

  const handleCreate = async () => {
    try {
      await api.post('/suppliers/', formData);
      fetchSuppliers();
      setIsCreateModalOpen(false);
    } catch (error) {
      console.error('Failed to create supplier:', error);
    }
  };

  const handleEdit = async () => {
    try {
      await api.put(`/suppliers/${formData.id}/`, formData);
      fetchSuppliers();
      setIsEditModalOpen(false);
    } catch (error) {
      console.error('Failed to edit supplier:', error);
    }
  };

  const handleOpenDeleteConfirm = (supplier) => {
    setSupplierToDelete(supplier);
    setIsConfirmationModalOpen(true);
  };

  const handleDeleteSupplier = async () => {
    if (!supplierToDelete) return;

    try {
      await api.delete(`/suppliers/${supplierToDelete.id}/`);
      fetchSuppliers();
    } catch (error) {
      console.error('Failed to delete supplier:', error);
    } finally {
      setIsConfirmationModalOpen(false);
      setSupplierToDelete(null);
    }
  };

  const handleViewDebt = (supplier) => {
    setSelectedSupplier(supplier);
    setIsDebtModalOpen(true);
  };

  const handleCloseDebtModal = () => {
    setIsDebtModalOpen(false);
    setSelectedSupplier(null);
    // Refresh suppliers to show updated debt amounts
    fetchSuppliers();
  };

  return (
    <Box position="relative" minH="100vh" bg={useColorModeValue('gray.50', 'gray.900')} overflowX="hidden">
      <Box position="relative" zIndex={1} p={{ base: 2, md: 8 }}>
        {/* Header Section - Modern Card */}
        <Box
          bg={cardBg}
          borderRadius={{ base: 'lg', md: '2xl' }}
          border="1px solid"
          borderColor={borderColor}
          p={{ base: 3, md: 8 }}
          mb={{ base: 4, md: 8 }}
          boxShadow={`0 20px 25px -5px ${shadowColor}, 0 10px 10px -5px ${shadowColor}`}
          position="relative"
          overflow="visible"
        >
          {/* Gradient accent */}
          <Box position="absolute" top={0} left={0} right={0} height="6px" bg="linear-gradient(90deg, #667eea, #764ba2, #f093fb, #f5576c)" />
          <Flex justify="space-between" align="center" wrap="wrap" gap={4} direction={{ base: 'column', md: 'row' }}>
            <Heading size={{ base: 'lg', md: 'xl' }} bgGradient="linear(to-r, #667eea, #764ba2)" bgClip="text" fontWeight="bold" textAlign={{ base: 'center', md: 'left' }}>
              🏭 Постачальники
            </Heading>
            <Button 
              leftIcon={<AddIcon />}
              bg="linear-gradient(135deg, #667eea, #764ba2)"
              color="white"
              size={{ base: 'md', md: 'lg' }}
              borderRadius="xl"
              px={{ base: 6, md: 8 }}
              boxShadow="0 4px 15px rgba(102, 126, 234, 0.4)"
              _hover={{ bg: 'linear-gradient(135deg, #5a6fd8, #6a4190)', transform: { base: 'none', md: 'translateY(-2px)' } }}
              _active={{ transform: 'translateY(0)' }}
              onClick={() => setIsCreateModalOpen(true)}
            >
              Додати постачальника
            </Button>
          </Flex>
        </Box>
        {/* Filters Section - Modern Card */}
        <Box 
          bg={filterBg}
          borderRadius={{ base: 'lg', md: 'xl' }}
          border="1px solid"
          borderColor={borderColor}
          p={{ base: 3, md: 6 }}
          mb={{ base: 4, md: 8 }}
          boxShadow={`0 10px 15px -3px ${shadowColor}`}
          position="relative"
          overflow="hidden"
        >
          <Box position="absolute" top={0} left={0} right={0} height="4px" bg="linear-gradient(90deg, #4facfe, #00f2fe)" />
          <HStack spacing={{ base: 2, md: 4 }} w="full">
            <Input
              placeholder="🔍 Пошук постачальників..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              bg={cardBg}
              border="1px solid"
              borderColor={borderColor}
              borderRadius="lg"
              size={{ base: 'md', md: 'lg' }}
              _focus={{ borderColor: '#4facfe', boxShadow: '0 0 0 1px rgba(79, 172, 254, 0.3)' }}
              _placeholder={{ color: subtleTextColor }}
            />
          </HStack>
        </Box>
        {/* Supplier Grid - Modern Card Container */}
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={{ base: 4, md: 6 }} mt={4}>
          <SupplierList
            suppliers={suppliers}
            onEdit={(supplier) => { setFormData(supplier); setIsEditModalOpen(true); }}
            onDelete={handleOpenDeleteConfirm}
            onViewDebt={handleViewDebt}
          />
        </SimpleGrid>
        {/* Modern Pagination */}
        {pagination.count > 0 && totalPages > 1 && (
          <Box bg={cardBg} borderRadius={{ base: 'lg', md: '2xl' }} border="1px solid" borderColor={borderColor} p={{ base: 4, md: 6 }} mt={{ base: 6, md: 8 }} boxShadow={`0 10px 15px -3px ${shadowColor}`}>
            <Flex justify="center" align="center">
              <HStack spacing={{ base: 2, md: 3 }} flexWrap="wrap" justify="center">
                <Button onClick={() => fetchPrevious()} isDisabled={!pagination.previous || pagination.currentPage <= 1} bg="linear-gradient(135deg, #667eea, #764ba2)" color="white" borderRadius="xl" size={{ base: 'sm', md: 'md' }} px={{ base: 3, md: 6 }} _hover={{ bg: 'linear-gradient(135deg, #5a6fd8, #6a4190)', transform: { base: 'none', md: 'translateY(-1px)' } }} _disabled={{ bg: 'gray.300', color: 'gray.500', transform: 'none' }}>
                  ← Попередня
                </Button>
                {visiblePages.map((page, index) => {
                  if (typeof page !== 'number') {
                    return (
                      <Box key={`${page}-${index}`} px={2} color={textColor}>
                        …
                      </Box>
                    );
                  }

                  const isActive = page === pagination.currentPage;
                  return (
                    <Button
                      key={page}
                      onClick={() => goToPage(page)}
                      size={{ base: 'sm', md: 'md' }}
                      minW={{ base: '36px', md: '40px' }}
                      px={{ base: 2, md: 3 }}
                      bg={isActive ? 'linear-gradient(135deg, #f093fb, #f5576c)' : cardBg}
                      color={isActive ? 'white' : textColor}
                      border="1px solid"
                      borderColor={isActive ? 'transparent' : borderColor}
                      borderRadius="lg"
                      _hover={{
                        bg: isActive ? 'linear-gradient(135deg, #e881f9, #f3455a)' : borderColor,
                        transform: { base: 'none', md: 'translateY(-1px)' },
                      }}
                      isDisabled={isActive}
                    >
                      {page}
                    </Button>
                  );
                })}
                <Button onClick={() => fetchNext()} isDisabled={!pagination.next} bg="linear-gradient(135deg, #667eea, #764ba2)" color="white" borderRadius="xl" size={{ base: 'sm', md: 'md' }} px={{ base: 3, md: 6 }} _hover={{ bg: 'linear-gradient(135deg, #5a6fd8, #6a4190)', transform: { base: 'none', md: 'translateY(-1px)' } }} _disabled={{ bg: 'gray.300', color: 'gray.500', transform: 'none' }}>
                  Наступна →
                </Button>
              </HStack>
            </Flex>
          </Box>
        )}
        {/* Modals */}
        <SupplierModals
          isCreateModalOpen={isCreateModalOpen}
          isEditModalOpen={isEditModalOpen}
          onCloseCreate={() => setIsCreateModalOpen(false)}
          onCloseEdit={() => setIsEditModalOpen(false)}
          onSubmitCreate={handleCreate}
          onSubmitEdit={handleEdit}
          formData={formData}
          setFormData={setFormData}
        />
        {/* Confirmation Modal */}
        <ConfirmationModal
          isOpen={isConfirmationModalOpen}
          onClose={() => setIsConfirmationModalOpen(false)}
          onConfirm={handleDeleteSupplier}
          message="Ви впевнені, що хочете видалити цього постачальника?"
        />
        {/* Debt Modal */}
        <DebtModal
          isOpen={isDebtModalOpen}
          onClose={handleCloseDebtModal}
          supplier={selectedSupplier}
        />
      </Box>
    </Box>
  );
};

export default Suppliers;
